let authToken: string | null = null;

/** Bearer token sent with every API request while signed in. Shared by all client instances. */
export const setAuthToken = (token: string | null) => {
    authToken = token;
};

/** Thrown for non-2xx responses. `body` is the parsed JSON error payload, when there is one. */
export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly body: unknown = null
    ) {
        super(message);
        this.name = "ApiError";
    }
}

export class ApiClient {
    private readonly baseURL: string;
    private controller: AbortController;
    private readonly cacheTtl = 60 * 1000;
    // Past cacheTtl a cached value is still served instantly, and refreshed in the background, until staleTtl
    private readonly staleTtl = 10 * 60 * 1000;
    private readonly maxCacheEntries = 100;
    private cache = new Map<string, { value: unknown; expiresAt: number; staleUntil: number }>();
    private inFlight = new Map<string, Promise<unknown>>();

    constructor(baseURL = "") {
        this.baseURL = baseURL;
        this.controller = new AbortController();
    }

    private async fetch<T>(url: string, options: RequestInit = {}): Promise<T> {
        const startTime = Date.now();
        const newOptions: RequestInit = {
            method: "GET",
            signal: this.controller.signal,
            ...options,
        };
        if (authToken) {
            newOptions.headers = {
                Authorization: `Bearer ${authToken}`,
                ...newOptions.headers,
            };
        }
        if (newOptions.body !== undefined && newOptions.body !== null) {
            newOptions.headers = {
                "Content-Type": "application/json",
                ...newOptions.headers,
            };
        }
        const response = await fetch(this.baseURL + url, newOptions);

        const endTime = Date.now();
        const time = endTime - startTime;
        if (time > 350) {
            console.warn(`Slow API Query - [${newOptions.method}] ${this.baseURL + url}; ${time}ms;`);
        }

        if (!response.ok) {
            const body = await response.json().catch(() => null);
            throw new ApiError(
                `API Error: ${response.status} ${response.statusText} - ${this.baseURL + url}`,
                response.status,
                body
            );
        }

        const json = await response.json();
        return json as T;
    }

    async get<T>(url: string): Promise<T> {
        // Account data depends on who is signed in, so never serve it from the shared cache
        if (url.startsWith("/auth/")) {
            return this.fetch<T>(url);
        }

        const cached = this.cache.get(url);
        const now = Date.now();
        if (cached && cached.expiresAt > now) {
            return cached.value as T;
        }
        if (cached && cached.staleUntil > now) {
            // Serve the stale value right away and refresh it for the next caller
            this.request<T>(url).catch(() => undefined);
            return cached.value as T;
        }
        if (cached) {
            this.cache.delete(url);
        }

        return this.request<T>(url);
    }

    private request<T>(url: string): Promise<T> {
        const pending = this.inFlight.get(url);
        if (pending) {
            return pending as Promise<T>;
        }

        const request = this.fetch<T>(url)
            .then((response) => {
                if (this.cache.size >= this.maxCacheEntries) {
                    const oldest = this.cache.keys().next().value;
                    if (oldest !== undefined) {
                        this.cache.delete(oldest);
                    }
                }
                const now = Date.now();
                this.cache.set(url, {
                    value: response,
                    expiresAt: now + this.cacheTtl,
                    staleUntil: now + this.staleTtl,
                });
                return response;
            })
            .finally(() => {
                this.inFlight.delete(url);
            });
        this.inFlight.set(url, request);
        return request;
    }

    async post<T>(url: string, payload: unknown = {}): Promise<T> {
        const response = await this.fetch<T>(url, {
            method: "POST",
            cache: "no-cache",
            body: JSON.stringify(payload),
        });
        this.cache.clear();
        return response;
    }

    async beacon(url: string, payload: BodyInit | null | undefined) {
        navigator.sendBeacon(this.baseURL + url, payload);
        return true;
    }

    abort() {
        this.controller.abort();
    }
}
